import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'utils/dayjs';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Icon from 'components/commons/Icon';

import {
  Item,
  MonthHeaderCell,
  MonthHeaderRow,
  MonthTitle,
  SummaryCell,
  SummaryDesktop,
  SummaryMobile,
  TriggerIcon,
  TriggerMain,
} from './styles';

const CurrencyYearArchiveMonthAccordion = ({
  month,
  year,
  summary,
  defaultExpanded,
  highlightedDate,
  columnCount,
  children,
}) => {
  const { locale } = useIntl();
  const panelId = `currency-year-archive-month-${year}-${month}`;
  const triggerId = `${panelId}-trigger`;

  const matchesHighlight = useMemo(() => {
    if (!highlightedDate) {
      return false;
    }

    const date = dayjs(highlightedDate, 'YYYY-MM-DD', true);

    return (
      date.isValid() &&
      String(date.year()) === String(year) &&
      date.month() + 1 === Number(month)
    );
  }, [highlightedDate, month, year]);

  const [isExpanded, setIsExpanded] = useState(
    matchesHighlight || defaultExpanded
  );

  useEffect(() => {
    if (matchesHighlight || defaultExpanded) {
      setIsExpanded(true);
    }
  }, [matchesHighlight, defaultExpanded]);

  const monthTitle = dayjs()
    .locale(locale)
    .year(Number(year))
    .month(month - 1)
    .format('MMMM');

  const monthHeading = `${monthTitle} ${year}`;

  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  const summaryColSpan = columnCount > 1 ? columnCount - 1 : 1;

  return (
    <Item id={panelId} $isExpanded={isExpanded}>
      <MonthHeaderRow
        id={triggerId}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
      >
        <MonthHeaderCell colSpan={summary ? 1 : columnCount}>
          <TriggerMain>
            <TriggerIcon $isExpanded={isExpanded} aria-hidden>
              <Icon name="arrowUp" />
            </TriggerIcon>
            <MonthTitle>{monthHeading}</MonthTitle>
          </TriggerMain>
          {summary ? <SummaryMobile>{summary}</SummaryMobile> : null}
        </MonthHeaderCell>
        {summary ? (
          <SummaryCell colSpan={summaryColSpan}>
            <SummaryDesktop>{summary}</SummaryDesktop>
          </SummaryCell>
        ) : null}
      </MonthHeaderRow>
      {children}
    </Item>
  );
};

CurrencyYearArchiveMonthAccordion.propTypes = {
  month: PropTypes.number.isRequired,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  summary: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  highlightedDate: PropTypes.string,
  columnCount: PropTypes.number,
  children: PropTypes.node,
};

CurrencyYearArchiveMonthAccordion.defaultProps = {
  summary: null,
  defaultExpanded: false,
  highlightedDate: null,
  columnCount: 3,
  children: null,
};

export default memo(CurrencyYearArchiveMonthAccordion);
